import React, { PureComponent } from 'react';
import _ from 'lodash';
import { dispatch, IRootDispatch, IRootState, state } from 'stores/rematch/root-store';
import { connect } from 'react-redux';
import { timeZone } from 'interfaces/timezone-type';
import { FilterType } from 'utilities/enum-utils';
import CommonUtils from 'utilities/common-utils';
import { ActiveFilterItem, MoreFilterMenu } from 'common-components/filter';
import { FilterTypeDateRange, IFilter } from 'interfaces/filter-interfaces';
import { Button, Divider, Group, Popover } from '@good/ui/core';
import { Filter } from '@good/icons';

export type IFilterSectionProps = {
  filters: IFilter[] | undefined | null;
  onChangeFilter: any;
  availableFilters: Array<FilterType>;
  displayTimezone: timeZone | null;
  services: typeof state.servicesStore.services;
  doFetchServicesLite: typeof dispatch.servicesStore.doFetchServicesLite;
  setHasBookingListingFilterChanged?: typeof dispatch.bookingsStore.setHasBookingListingFilterChanged;
  containerClassName?: string;
  usePortal?: boolean;
  verticalDisplay?: boolean;
  displayMoreFilter?: boolean;
  leftSection?: React.ReactNode;
  dateFilterV2?: boolean;
};

type IFilterSectionState = {
  openedMenu: string | null;
  addNewFilterPanel: string | null;
  addFilterOpened: boolean;
};

class FilterSection extends PureComponent<IFilterSectionProps, IFilterSectionState> {
  override state = {
    openedMenu: null,
    addNewFilterPanel: null,
    addFilterOpened: false,
  };

  private _addFilter = (filterName: FilterType) => {
    if (CommonUtils.findFilter(filterName, this.props.filters)) {
      this._setIsOpen(filterName, true);
    } else {
      this.setState({ addNewFilterPanel: filterName });
    }
  };

  private _saveNewFilterValue = async (
    filterType: Exclude<FilterType, FilterTypeDateRange>,
    newFilterValue: any,
    selectionLabel: string,
  ) => {
    const newFilters = this.props.filters ? _.clone(this.props.filters) : [];
    const filterIndex = _.findIndex(newFilters, (filter: IFilter) => filter.filter === filterType);
    if (filterIndex !== -1) {
      newFilters.splice(filterIndex, 1, { filter: filterType, values: newFilterValue, selectionLabel });
    } else {
      newFilters.push({ filter: filterType, values: newFilterValue, selectionLabel });
    }
    await this.props.onChangeFilter(newFilters);
  };

  private _removeFilter = (filterType: FilterType) => {
    this.setState({ openedMenu: null });
    this.props.onChangeFilter(_.filter(this.props.filters, (data) => data.filter !== filterType));
  };

  private _setIsOpen = (filterType: FilterType, isOpen: boolean) => {
    this.setState({ openedMenu: isOpen ? filterType : null });
  };

  override render() {
    const {
      displayTimezone,
      filters,
      containerClassName,
      usePortal = true,
      verticalDisplay = false,
      displayMoreFilter = true,
    } = this.props;
    const { openedMenu } = this.state;

    return (
      <div className={containerClassName}>
        <Group gap='xs'>
          {this.props.leftSection}
          {displayMoreFilter && (
            <Popover
              shadow='md'
              radius='md'
              opened={this.state.addFilterOpened}
              onChange={(o) => this.setState({ addFilterOpened: o })}
            >
              <Popover.Target>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => this.setState({ addFilterOpened: !this.state.addFilterOpened })}
                  leftSection={<Filter />}
                >
                  Add filter
                </Button>
              </Popover.Target>
              <Popover.Dropdown p={0} style={{ overflow: 'hidden' }}>
                <MoreFilterMenu
                  availableFilters={this.props.availableFilters}
                  filters={filters}
                  saveNewFilterValue={this._saveNewFilterValue}
                  removeFilter={this._removeFilter}
                  addFilter={this._addFilter}
                  addNewFilterPanel={this.state.addNewFilterPanel}
                  displayTimezone={displayTimezone}
                  onClose={() => this.setState({ addFilterOpened: false })}
                  onChangeAddNewFilterPanel={(filterType) => this.setState({ addNewFilterPanel: filterType })}
                />
              </Popover.Dropdown>
            </Popover>
          )}
          <Divider orientation='vertical' my={4} />
          {filters?.map?.((filter, idx) => {
            if (_.find(FilterType, (f) => f === filter.filter)) {
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                <React.Fragment key={idx}>
                  <ActiveFilterItem
                    filter={filter}
                    saveNewFilterValue={this._saveNewFilterValue}
                    removeFilter={this._removeFilter}
                    isOpen={openedMenu === filter.filter}
                    setIsOpen={this._setIsOpen}
                    displayTimezone={this.props.displayTimezone}
                    usePortal={usePortal}
                    canRemove={displayMoreFilter}
                    dateFilterV2={this.props.dateFilterV2}
                  />
                  {verticalDisplay && <br />}
                </React.Fragment>
              );
            }

            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            return <React.Fragment key={idx} />;
          })}
        </Group>
      </div>
    );
  }
}

const mapState = (state: IRootState) => ({
  services: state.servicesStore.services,
});

const mapDispatch = (dispatch: IRootDispatch) => ({
  doFetchServicesLite: dispatch.servicesStore.doFetchServicesLite,
});

export default connect(mapState, mapDispatch)(FilterSection);
